import { type AutoEmptyTrashDays, db } from "@db";
import { resetAllData, updateSettings } from "@db/settings";
import { useAutoSave } from "@hooks/use-auto-save";
import { useStorageQuota } from "@hooks/use-storage-quota";
import { formatBytes } from "@lib/numbers";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@ui/alert-dialog";
import { Button, buttonVariants } from "@ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@ui/field";
import { SaveIndicator } from "@ui/save-indicator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";
import { Slider } from "@ui/slider";
import { useLiveQuery } from "dexie-react-hooks";
import { useCallback, useEffect, useState } from "react";

export const Route = createFileRoute("/settings/storage")({
  component: StorageSettingsComponent,
});

const GB = 1024 ** 3;
const MIN_LIMIT = 1 * GB;

const autoEmptyOptions: { value: AutoEmptyTrashDays; label: string }[] = [
  { value: 0, label: "Never" },
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
];

function StorageSettingsComponent() {
  const settings = useLiveQuery(() => db.settings.get("settings"));
  const { usage: storageUsage } = useStorageQuota();
  const [storageQuota, setStorageQuota] = useState<number>(10 * GB);
  const [isPersisted, setIsPersisted] = useState<boolean | null>(null);
  const [storageLimit, setStorageLimit] = useState(0);

  const save = useCallback(
    async (value: number) => {
      if (settings) {
        await updateSettings({ storageLimit: value });
      }
    },
    [settings],
  );

  const { onBlur, showSaved } = useAutoSave({ save });

  useEffect(() => {
    if (settings) {
      setStorageLimit(settings.storageLimit);
    }
  }, [settings]);

  useEffect(() => {
    async function fetchStorage() {
      if (navigator.storage?.estimate) {
        const [estimate, persisted] = await Promise.all([
          navigator.storage.estimate(),
          navigator.storage.persisted?.() ?? Promise.resolve(false),
        ]);
        const quota = estimate.quota ?? 10 * GB;
        setStorageQuota(Math.max(quota, MIN_LIMIT));

        if (!persisted && navigator.storage.persist) {
          const result = await navigator.storage.persist();
          setIsPersisted(result);
        } else {
          setIsPersisted(persisted);
        }
      }
    }
    fetchStorage();
  }, []);

  return (
    <div className="max-w-xl p-6">
      <FieldSet>
        <FieldLegend>Usage</FieldLegend>
        <div className="space-y-1 text-sm">
          <div className="flex items-center gap-2">
            <span>
              {formatBytes(storageUsage)} / {formatBytes(storageQuota)} (
              {storageQuota > 0
                ? ((storageUsage / storageQuota) * 100).toFixed(1)
                : "0"}
              %)
            </span>
            {storageLimit > 0 && storageUsage > storageLimit && (
              <span className="font-medium text-red-600">Over Limit</span>
            )}
          </div>
          {isPersisted !== null && (
            <div>
              {isPersisted ? (
                <span className="text-green-600">
                  Persistent — your data is protected and won't be automatically
                  deleted
                </span>
              ) : (
                <span className="text-amber-600">
                  Temporary — the browser may delete your data if storage runs
                  low
                </span>
              )}
            </div>
          )}
        </div>
      </FieldSet>
      <FieldSet>
        <FieldLegend className="flex items-center gap-2">
          Limit
          <SaveIndicator visible={showSaved} />
        </FieldLegend>
        <FieldGroup>
          <Field>
            <FieldLabel>{formatBytes(storageLimit, 0)}</FieldLabel>
            <Slider
              min={MIN_LIMIT}
              max={storageQuota}
              step={GB}
              value={[storageLimit]}
              onValueChange={([value]) => setStorageLimit(value)}
              onValueCommit={([value]) => onBlur(value)}
            />
          </Field>
        </FieldGroup>
      </FieldSet>
      <FieldSet>
        <FieldLegend>Auto-Empty Trash</FieldLegend>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="autoEmptyTrash">
              Permanently delete trashed items after
            </FieldLabel>
            <FieldDescription>
              Items in the trash older than this will be permanently deleted
              when the app starts.
            </FieldDescription>
            <Select
              value={String(settings?.autoEmptyTrashDays ?? 30)}
              onValueChange={(value) =>
                updateSettings({
                  autoEmptyTrashDays: Number(value) as AutoEmptyTrashDays,
                })
              }
            >
              <SelectTrigger id="autoEmptyTrash" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {autoEmptyOptions.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </FieldGroup>
      </FieldSet>
      <FieldSet>
        <FieldLegend>Reset</FieldLegend>
        <p className="text-muted-foreground text-sm">
          Delete all local data and reset the application to a fresh state. This
          removes all files, sequences, alignments, and settings.
        </p>
        <ResetDataDialog />
      </FieldSet>
    </div>
  );
}

function ResetDataDialog() {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" className="mt-2 self-start">
          Reset all data
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset all data?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete all your local data including files,
            sequences, alignments, and settings. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: "destructive" })}
            onClick={resetAllData}
          >
            Reset
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
